import React, { useState } from 'react';
import useAnimationStore from '../contexts/AnimationStore';
import {
  Menu,
  X,
  LogOut,
  LogIn,
  Video,
  Image,
  MessageSquare,
  Sparkles,
  FolderOpen,
  Wand2,
  History as HistoryIcon,
  Calendar,
  Palette,
  HelpCircle,
  Crown,
  Film,
  Users,
  Settings,
  FileText,
  Chrome,
  FileText as BlogIcon,
  BookOpen
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useToast } from '../contexts/ToastContext';
import { UserDropdown } from './UserDropdown';
import { useNavigate, Link } from 'react-router-dom';
import { openHelpPage } from '../utils/navigation';
import { useAuth } from '../contexts/AuthContext';

const Header = ({
  isLoggedIn,
  isPremiumUser,
  currentView,
  onViewChange,
  onSettingsClick,
  onLoginClick,
  onLogoutClick,
  isVideoMode,
  onVideoModeToggle,
  isSettingsOpen
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isGuestDropdownOpen, setIsGuestDropdownOpen] = useState(false);
  const { addToast } = useToast();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // TEMPORARY FIX: Get animation state
  const isAnimationGenerating = useAnimationStore(state => state.isGenerating);
  const activeTab = useAnimationStore(state => state.activeTab);

  // Navigation items
  const navigationItems = [
    { id: 'chat', label: 'Prompt Lab', icon: MessageSquare },
    { id: 'generate', label: 'Generate', icon: Sparkles },
    { id: 'animate', label: 'Animate', icon: Film },
    { id: 'collections', label: 'Collections', icon: FolderOpen },
    { id: 'history', label: 'History', icon: HistoryIcon },
    { id: 'weekly', label: 'Weekly Prompts', icon: Calendar },
    { id: 'styleRefs', label: 'Style Codes', icon: Palette },
    { id: 'tutorials', label: 'Tutorials', icon: BookOpen, external: true },
   
  ];

  // Guest dropdown menu items
  const guestMenuItems = [
    //{ id: 'videoMode', label: 'Switch to Video Mode', icon: Video, action: handleVideoModeToggle },
   
    { id: 'help', label: 'Help', icon: HelpCircle, action: openHelpPage },
    { id: 'terms', label: 'Terms & Privacy', icon: FileText, action: () => window.open('https://promptcatalyst.ai/legal', '_blank') },
    { id: 'extension', label: 'Get Browser Extension', icon: Chrome, action: () => window.open('https://chromewebstore.google.com/detail/prompt-catalyst/hehieakgdbakdajfpekgmfckplcjmgcf', '_blank') }
  ];

  function handleVideoModeToggle() {
    if (!isVideoMode) {
      addToast('Video mode will be available soon!', 'info', 3000);
      return;
    }
    onVideoModeToggle();
    setIsGuestDropdownOpen(false);
  };

  // Premium badge component
  const PremiumBadge = () => {
    return (
      <div
        className={`flex items-center space-x-1 px-2 py-0.5 rounded-full text-xs font-semibold text-white 
          ${user?.isUltimateMember
            ? 'bg-gradient-to-r from-teal-400 via-blue-500 to-indigo-500'
            : user?.isProMember
              ? 'bg-gradient-to-r from-blue-400 via-indigo-500 to-purple-500'
              : 'bg-gradient-to-r from-pink-400 via-purple-500 to-blue-500'} ml-0.5 translate-y-0.5`}
      >
        <span>{user?.isUltimateMember ? 'Visionary' : user?.isProMember ? 'Pro' : 'Standard'}</span>
      </div>
    );
  };

  // Handle mobile nav item selection
  const handleMobileNavigation = (viewId, isExternal) => {
    if (isExternal) {
      navigate('/tutorials');
    } else {
      onViewChange(viewId);
    }
    setIsMobileMenuOpen(false);
  };

  // Guest settings dropdown - closes when clicking outside
  const closeGuestDropdown = () => {
    setIsGuestDropdownOpen(false);
  };

  // Toggle guest settings dropdown
  const toggleGuestDropdown = (e) => {
    e.stopPropagation();
    setIsGuestDropdownOpen(!isGuestDropdownOpen);
  };

  // Add click outside listener for guest dropdown
  React.useEffect(() => {
    if (isGuestDropdownOpen) {
      const handleClickOutside = (e) => {
        if (e.target.closest('.guest-dropdown-container') === null) {
          closeGuestDropdown();
        }
      };
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [isGuestDropdownOpen]);

  return (
    <>
      {/* HEADER */}
      <header className="relative z-30 flex h-16 items-center px-4 md:px-6 bg-gradient-to-r from-[var(--cardBackground)]/95 via-[var(--cardBackground)]/90 to-[var(--cardBackground)]/80 border-b border-[var(--border)] backdrop-blur-md shadow-sm transition-colors duration-300">
        {/* LOGO SECTION */}
        <div className="flex items-center min-w-[180px] md:w-1/4 lg:min-w-[220px] xl:min-w-[240px] shrink-0">
          <div className="flex-shrink-0 flex items-center justify-center w-10 h-10">
            <img
              src="/logo.png"
              alt="Prompt Catalyst"
              className="h-full w-full object-contain mt-[1px]"
            />
          </div>
          <div className="flex items-center ml-[6px] overflow-hidden">
            <h1 className="text-lg md:text-xl font-semibold text-[var(--text)] truncate">
              Prompt Catalyst
            </h1>
            <AnimatePresence>
              {isPremiumUser && (
                <motion.div
                  key="premium-badge"
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -10 }}
                  transition={{ duration: 0.2 }}
                  className="ml-2 flex-shrink-0 hidden md:hidden lg:flex"
                >
                  <PremiumBadge />
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>

        {/* DESKTOP NAVIGATION - TRUE CENTERING */}
        <nav className="hidden lg:flex items-center justify-center absolute inset-0 pointer-events-none">
          <div className="flex items-center justify-center rounded-lg bg-[var(--dropdownHover)]/40 w-[600px] pointer-events-auto">
            <div className="flex items-center justify-center w-full px-3">
              {navigationItems.map((item) => {
              const Icon = item.icon;
              const isActive = currentView === item.id;
              return (
              <motion.button
              key={item.id}
              onClick={() => item.external ? navigate('/tutorials') : onViewChange(item.id)}
              className={`
              relative
              flex items-center justify-center
              mx-2
              px-3 py-1.5
              rounded-md
              text-sm font-medium
              transition-colors duration-200
              ${isActive
              ? 'text-[var(--cardBackground)]'
              : 'text-[var(--textSecondary)] hover:text-[var(--text)] hover:bg-[var(--dropdownHover)]/50'}
              `}
              style={{ minWidth: '36px' }}
              >
                    <div className="flex items-center justify-center">
                      <Icon
                        size={18}
                        className={
                          isActive
                            ? 'text-[var(--cardBackground)]'
                            : 'text-[var(--textSecondary)]'
                        }
                      />
                      {isActive && (
                        <motion.span
                          initial={{ width: 0, opacity: 0 }}
                          animate={{ width: 'auto', opacity: 1 }}
                          exit={{ width: 0, opacity: 0 }}
                          transition={{ duration: 0.3 }}
                          style={{ overflow: 'hidden', whiteSpace: 'nowrap' }}
                          className="ml-2"
                        >
                          {item.label}
                        </motion.span>
                      )}
                    </div>
                    {isActive && (
                      <motion.div
                        layoutId="activeTab"
                        className="absolute inset-0 bg-[var(--primary)] rounded-full -z-10"
                        transition={{
                          type: 'spring',
                          bounce: 0.2,
                          duration: 0.6
                        }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* TABLET NAVIGATION - Icons only, fixed grid */}
        <nav className="hidden md:flex lg:hidden items-center justify-center w-2/4">
          <div className="flex items-center justify-center rounded-lg bg-[var(--dropdownHover)]/40 max-w-[450px] w-full">
            <div className="flex items-center justify-center w-full px-2">
              {navigationItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentView === item.id;
                return (
                  <motion.button
                    key={item.id}
                    onClick={() => item.external ? navigate('/tutorials') : onViewChange(item.id)}
                    className={`
                      relative
                      flex items-center justify-center
                      mx-1
                      px-2 py-1.5
                      rounded-md
                      text-sm font-medium
                      transition-colors duration-200
                      ${isActive
                        ? 'text-[var(--cardBackground)]'
                        : 'text-[var(--textSecondary)] hover:text-[var(--text)] hover:bg-[var(--dropdownHover)]/50'}
                    `}
                    style={{ minWidth: '36px' }}
                    title={item.label}
                  >
                    <div className="flex items-center justify-center">
                      <Icon
                        size={18}
                        className={
                          isActive
                            ? 'text-[var(--cardBackground)]'
                            : 'text-[var(--textSecondary)]'
                        }
                      />
                    </div>
                    {isActive && (
                      <motion.div
                        layoutId="activeTabTablet"
                        className="absolute inset-0 bg-[var(--primary)] rounded-full -z-10"
                        transition={{
                          type: 'spring',
                          bounce: 0.2,
                          duration: 0.6
                        }}
                      />
                    )}
                  </motion.button>
                );
              })}
            </div>
          </div>
        </nav>

        {/* RIGHT SECTION */}
        <div className="flex items-center gap-2 ml-auto md:w-1/4 lg:w-auto justify-end lg:mr-4">

          {/* MOBILE SETTINGS & MENU */}
          <div className="flex items-center gap-2 md:hidden">
            {/* Mobile Settings Button - only show in chat view */}
            <AnimatePresence>
              {!isSettingsOpen && currentView === 'chat' && onSettingsClick && (
                <motion.button
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.2 }}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={onSettingsClick}
                  className="flex p-2 rounded-lg text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
                  title="Settings"
                >
                  <Wand2 size={20} />
                </motion.button>
              )}
            </AnimatePresence>

            {/* Help Button */}
            <motion.button
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.2 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={openHelpPage}
              className="flex p-2 rounded-lg text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
              title="Help"
            >
              <HelpCircle size={20} />
            </motion.button>

            {/* Mobile Menu Toggle */}
            <button
              className="p-2 text-[var(--textSecondary)] hover:text-[var(--text)] rounded-lg hover:bg-[var(--dropdownHover)] transition-all duration-200"
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              <motion.div
                animate={{ rotate: isMobileMenuOpen ? 90 : 0 }}
                transition={{ duration: 0.2 }}
              >
                {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </motion.div>
            </button>
          </div>

          {/* Premium badge for tablet only - separate from title */}
          <AnimatePresence>
            {isPremiumUser && (
              <motion.div
                key="premium-badge-tablet"
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                className="ml-1 hidden md:flex lg:hidden"
              >
                <PremiumBadge />
              </motion.div>
            )}
          </AnimatePresence>

          {/* Blog Link 
          <Link 
            to="/blog"
            className="hidden md:flex items-center gap-2 px-3 py-1.5 rounded-lg text-[var(--textSecondary)] hover:text-[var(--text)] hover:bg-[var(--dropdownHover)]/50 transition-all duration-200 mr-2"
          >
            <BlogIcon size={16} />
            <span className="hidden xl:inline">Blog</span>
          </Link>
*/}
          {/* DESKTOP SETTINGS BUTTON - only show in chat view */}
          <AnimatePresence>
            {!isSettingsOpen && currentView === 'chat' && onSettingsClick && (
              <motion.button
                initial={{ opacity: 0, scale: 0.8 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.8 }}
                transition={{ duration: 0.2 }}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onSettingsClick}
                className="hidden md:flex items-center justify-center p-2 rounded-lg text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
                title="Settings"
              >
                <Wand2 size={18} />
              </motion.button>
            )}
          </AnimatePresence>

          {/* DESKTOP AUTH SECTION */}
          {!isLoggedIn ? (
            <div className="hidden md:flex items-center gap-2">
              {/* Settings Gear Dropdown for Guest Users */}
              <div className="guest-dropdown-container relative">
                <motion.button
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={toggleGuestDropdown}
                  className="flex items-center justify-center p-2 rounded-lg text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
                  title="Options"
                >
                  <Settings size={18} />
                </motion.button>

                {/* Guest Dropdown Menu */}
                <AnimatePresence>
                  {isGuestDropdownOpen && (
                    <motion.div
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.2 }}
                      className="absolute right-0 mt-2 w-56 rounded-lg bg-[var(--cardBackground)] border border-[var(--border)] shadow-lg overflow-hidden z-50"
                    >
                      <div className="py-1">
                        {guestMenuItems.map((item) => (
                          <button
                            key={item.id}
                            onClick={item.action}
                            className="flex items-center gap-3 w-full px-4 py-2 text-sm text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-colors duration-200"
                          >
                            <item.icon size={16} />
                            <span>{item.label}</span>
                          </button>
                        ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              {/* Login Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onLoginClick}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gradient-to-r from-[var(--primary)] to-[var(--secondary)] text-black text-sm font-medium hover:opacity-90 transition-all duration-200 shadow-md shadow-[var(--primary)]/20"
              >
                <LogIn size={16} />
                <span className="hidden xl:inline">Log In</span>
              </motion.button>
            </div>
          ) : (
            <div className="hidden md:block">
              <UserDropdown
                onLogout={onLogoutClick}
                onPremiumClick={onLoginClick}
              />
            </div>
          )}
        </div>
      </header>

      {/* MOBILE NAVIGATION MENU */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black/20 backdrop-blur-sm z-20 md:hidden"
              onClick={() => setIsMobileMenuOpen(false)}
            />

            {/* Menu Content */}
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.2 }}
              className="fixed inset-x-0 top-16 z-20 md:hidden bg-[var(--cardBackground)] border-b border-[var(--border)] overflow-y-auto max-h-[calc(100vh-4rem)] shadow-md"
            >
              <nav className="px-4 py-2 space-y-1">
                {navigationItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = currentView === item.id;
                  return (
                    <motion.button
                      key={item.id}
                      onClick={() => handleMobileNavigation(item.id, item.external)}
                      className={`
                        w-full flex items-center gap-3
                        px-4 py-3
                        rounded-lg
                        text-sm font-medium
                        transition-colors duration-200
                        ${isActive
                          ? 'bg-[var(--primary)] text-[var(--cardBackground)]'
                          : 'text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)]/50 hover:text-[var(--text)]'}
                      `}
                    >
                      <Icon
                        size={20}
                        className={isActive ? 'text-[var(--cardBackground)]' : ''}
                      />
                      <span>{item.label}</span>
                    </motion.button>
                  );
                })}

                <div className="border-t border-[var(--border)] my-2" />

                {/* Guest Menu Items in Mobile View */}
                {!isLoggedIn && (
                  <>
                    {guestMenuItems.map((item) => (
                      <motion.button
                        key={item.id}
                        onClick={() => {
                          item.action();
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
                      >
                        <item.icon size={20} />
                        <span>{item.label}</span>
                      </motion.button>
                    ))}
                  </>
                )}

                {/* Auth Section - Mobile */}
                {!isLoggedIn ? (
                  <motion.button
                    onClick={() => {
                      onLoginClick();
                      setIsMobileMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium text-[var(--textSecondary)] hover:bg-[var(--dropdownHover)] hover:text-[var(--text)] transition-all duration-200"
                  >
                    <LogIn size={20} />
                    <span>Login</span>
                  </motion.button>
                ) : (
                  <UserDropdown
                    onLogout={() => {
                      onLogoutClick();
                      setIsMobileMenuOpen(false);
                    }}
                    onPremiumClick={() => {
                      onLoginClick();
                      setIsMobileMenuOpen(false);
                    }}
                    isMobile={true}
                  />
                )}
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </>
  );
};

export default Header;